import {
  LogOut,
  MessageCircleMore,
  Settings,
  User,
  Wallet,
} from 'lucide-react';
import { useNavigate } from 'react-router';

import { MainChat } from '@/components/layouts/main-chat';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { useCustomerPortalSession } from '@/features/checkout/api/create-customer-portal-session';
import { useLogout } from '@/global/auth/api/useLogout';
import { UserAvatar } from '@/global/auth/components/UserAvatar';
import { useUnreadMessagesCount } from '@/global/chat/api/getUnreadMessagesCount';
import { useSession } from '@/hooks/useSession';
import useConversationStore from '@/stores/Conversation';
import { useSignInDialog } from '@/stores/SignInDialog';

export const UserNavigation = () => {
  const { data: session, error, isLoading, isLoggedIn } = useSession();
  const navigate = useNavigate();
  const { setIsOpen } = useConversationStore();
  const { mutate: logout } = useLogout();
  const { data: unreadMessagesCountData } = useUnreadMessagesCount({
    queryConfig: {
      enabled: !!isLoggedIn,
    },
  });
  const { open: openSignInDialog } = useSignInDialog();

  const { mutate: openPortal } = useCustomerPortalSession({
    mutationConfig: {
      onSuccess: (data) => {
        setTimeout(() => {
          window.open(data.url, '_blank');
        });
      },
    },
  });

  if (error || isLoading) {
    return (
      <div className="flex flex-row items-center justify-end gap-2">
        <Skeleton className="inline-flex size-9 rounded-full" />
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div className="flex flex-row items-center gap-2">
        <Button variant="outline" onClick={openSignInDialog}>
          Log In
        </Button>
        <Button onClick={() => navigate('/register')}>Sign Up</Button>
      </div>
    );
  }

  return (
    <div className="flex flex-row items-center justify-end gap-2">
      <MainChat />
      <DropdownMenu>
        <DropdownMenuTrigger className="inline-flex size-9 shrink-0 items-center justify-center gap-2 rounded-full align-middle text-xs font-medium  transition-all hover:bg-white/20 focus:outline-hidden focus:ring-2 focus:ring-secondary focus:ring-offset-2 focus:ring-offset-primary">
          <div className="relative">
            <UserAvatar />
            {unreadMessagesCountData &&
              unreadMessagesCountData?.unreadMessagesCount > 0 && (
                <span className="absolute end-0 top-0 z-10 -me-1 -mt-1 flex">
                  <span className="absolute inline-flex size-full animate-ping rounded-full bg-destructive opacity-75"></span>
                  <span className="relative inline-flex size-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] text-white">
                    {unreadMessagesCountData.unreadMessagesCount}
                  </span>
                </span>
              )}
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuGroup>
            <DropdownMenuLabel>Account</DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => navigate(`/profile/${session?.user?.id}`)}
            >
              <User size={16} />
              My Profile
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/profilesettings')}>
              <Settings size={16} />
              Settings
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => openPortal(undefined)}>
              <Wallet size={16} />
              Billing
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setIsOpen(true)}>
              <MessageCircleMore size={16} />
              Messages
              {unreadMessagesCountData &&
                unreadMessagesCountData?.unreadMessagesCount > 0 && (
                  <span className="ml-2 inline-flex size-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] text-white">
                    {unreadMessagesCountData.unreadMessagesCount}
                  </span>
                )}
            </DropdownMenuItem>
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <DropdownMenuItem onClick={() => logout(undefined)}>
              <LogOut size={16} />
              Logout
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
